import { useState } from 'react';

import { useGetUserAnalysisStatsCsvQuery } from '@/shared/hooks/queries/generated';

interface UseExportAnalysisStatsReturn {
  exportAnalysisStats: () => Promise<void>;
  isExporting: boolean;
  error: string | null;
}

/**
 * Hook pour exporter les statistiques des analyses de l'utilisateur au format CSV
 */
export const useExportAnalysisStats = (): UseExportAnalysisStatsReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportAnalysisStats = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Appel GraphQL pour récupérer le CSV
      const result = await useGetUserAnalysisStatsCsvQuery.fetcher()();

      if (!result?.getUserAnalysisStatsCsv) {
        throw new Error('Aucune donnée reçue');
      }

      const csvContent = result.getUserAnalysisStatsCsv;

      // Créer un blob avec le contenu CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      // Créer un lien de téléchargement temporaire
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Générer un nom de fichier avec la date actuelle
      const date = new Date().toISOString().split('T')[0];
      const fileName = `recop-analyses-stats-${date}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';

      // Ajouter le lien au DOM, cliquer dessus et le retirer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Libérer la mémoire
      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Erreur lors de l'export";
      setError(errorMessage);
      console.error('Error exporting analysis stats:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportAnalysisStats,
    isExporting,
    error,
  };
};
